// src/features/tournament/useTournamentStructure.js
import { useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { getDefaultTournamentData } from '../../utils/defaultTournamentData';
import { reorderColumns } from '../../utils/structureUtils';
import { useAppConfig } from '../../contexts/AppConfigContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useLocale } from '../../contexts/LocaleContext';

const timeStringToSeconds = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return 0;
  const parts = timeString.split(':').map(Number);
  return parts.length === 2 ? parts[0] * 60 + parts[1] : 0;
};

export const useTournamentStructure = ({
  stateRef,
  setTournamentState,
  setIsReadyEditing,
  tournamentId,
  syncStructureToDB,
  updateDetails,
  updateName
}) => {
  const { config: appConfig } = useAppConfig();
  const { showNotification } = useNotification();
  const { t } = useLocale();

  const touchStructure = useCallback(() => setIsReadyEditing(true), [setIsReadyEditing]);

  const updateRowValue = useCallback((tableRowIndex, key, value) => {
    setTournamentState(prev => {
      const newRows = [...prev.structure.levels];
      newRows[tableRowIndex] = { ...newRows[tableRowIndex], [key]: value };
      const newState = { ...prev, structure: { ...prev.structure, levels: newRows } };

      if (prev.timer_state.state === 'ready' && tableRowIndex === 0 && key === 'time') {
        const newLevelTime = timeStringToSeconds(newRows[0]?.time);
        newState.timer_state = { ...prev.timer_state, remaining: newLevelTime || 0, pausedAt: newLevelTime || 0 };
      }
      return newState;
    });
    touchStructure();
  }, [setTournamentState, touchStructure]);

  const recalculateLevels = (rows) => {
    let levelCount = 0;
    return rows.map(row => {
      if (row.type === 'level') {
        levelCount++;
        return { ...row, level: levelCount };
      }
      return row;
    });
  };

  const insertRowAfter = useCallback((tableRowIndex) => {
    setTournamentState(prev => {
      const newRows = [...prev.structure.levels];
      const currentRow = newRows[tableRowIndex];
      // reset specific fields for the new row to avoid confusion
      const newRow = {
        ...currentRow,
        id: `row-${Date.now()}`,
        // specific cleanup if needed, but keeping values is often desired for convenience
      };
      newRows.splice(tableRowIndex + 1, 0, newRow);

      const updatedRows = recalculateLevels(newRows);

      // Shift events down for rows after the insertion point
      const currentEvents = prev.structure.events || [];
      const updatedEvents = currentEvents.map(e =>
        e.rowIndex > tableRowIndex ? { ...e, rowIndex: e.rowIndex + 1 } : e
      );

      const newState = { ...prev, structure: { ...prev.structure, levels: updatedRows, events: updatedEvents } };
      if (prev.timer_state.state === 'ready') {
        const newLevelTime = timeStringToSeconds(updatedRows[0]?.time);
        newState.timer_state = { ...prev.timer_state, remaining: newLevelTime || 0, pausedAt: newLevelTime || 0 };
      }
      return newState;
    });
    touchStructure();
  }, [setTournamentState, touchStructure]);

  const deleteRow = useCallback((tableRowIndex) => {
    const currentState = stateRef.current;
    if (!currentState) return;

    if (currentState.structure.levels.length <= 1) {
      showNotification(t('errors.cannotDeleteLastRow'), 'warning');
      return;
    }

    setTournamentState(prev => {
      const remainingRows = prev.structure.levels.filter((_, i) => i !== tableRowIndex);
      const updatedRows = recalculateLevels(remainingRows);

      // Remove events for the deleted row and shift events up for subsequent rows
      const currentEvents = prev.structure.events || [];
      const updatedEvents = currentEvents
        .filter(e => e.rowIndex !== tableRowIndex)
        .map(e => e.rowIndex > tableRowIndex ? { ...e, rowIndex: e.rowIndex - 1 } : e);

      const newState = { ...prev, structure: { ...prev.structure, levels: updatedRows, events: updatedEvents } };
      if (prev.timer_state.state === 'ready') {
        const newLevelTime = timeStringToSeconds(updatedRows[0]?.time);
        newState.timer_state = { ...prev.timer_state, remaining: newLevelTime || 0, pausedAt: newLevelTime || 0 };
      }
      return newState;
    });
    touchStructure();
  }, [setTournamentState, touchStructure, showNotification, t, stateRef]);

  const insertBreakRow = useCallback((tableRowIndex) => {
    setTournamentState(prev => {
      const newRows = [...prev.structure.levels];
      const newBreakRow = { id: `row-break-${Date.now()}`, type: "break", level: "break", time: '10:00' };
      prev.structure.columns.forEach(col => { if (!newBreakRow.hasOwnProperty(col.label)) newBreakRow[col.label] = '—'; });
      newRows.splice(tableRowIndex + 1, 0, newBreakRow);

      const updatedRows = recalculateLevels(newRows);

      // Shift events down for rows after the insertion point
      const currentEvents = prev.structure.events || [];
      const updatedEvents = currentEvents.map(e =>
        e.rowIndex > tableRowIndex ? { ...e, rowIndex: e.rowIndex + 1 } : e
      );

      const newState = { ...prev, structure: { ...prev.structure, levels: updatedRows, events: updatedEvents } };
      if (prev.timer_state.state === 'ready') {
        const newLevelTime = timeStringToSeconds(updatedRows[0]?.time);
        newState.timer_state = { ...prev.timer_state, remaining: newLevelTime || 0, pausedAt: newLevelTime || 0 };
      }
      return newState;
    });
    touchStructure();
  }, [setTournamentState, touchStructure]);

  const handleRowDragEnd = useCallback((active, over) => {
    if (!over || active.id === over.id) return;
    setTournamentState(prev => {
      const oldIndex = prev.structure.levels.findIndex(r => r.id === active.id);
      const newIndex = prev.structure.levels.findIndex(r => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const newRows = arrayMove(prev.structure.levels, oldIndex, newIndex);

      const updatedRows = recalculateLevels(newRows);

      // Update event rowIndexes to match the move
      const currentEvents = prev.structure.events || [];
      const updatedEvents = currentEvents.map(e => {
        if (e.rowIndex === oldIndex) return { ...e, rowIndex: newIndex };

        if (oldIndex < newIndex) {
          // Moved down: items between old and new shift up
          if (e.rowIndex > oldIndex && e.rowIndex <= newIndex) {
            return { ...e, rowIndex: e.rowIndex - 1 };
          }
        } else {
          // Moved up: items between new and old shift down
          if (e.rowIndex >= newIndex && e.rowIndex < oldIndex) {
            return { ...e, rowIndex: e.rowIndex + 1 };
          }
        }
        return e;
      });

      const newState = { ...prev, structure: { ...prev.structure, levels: updatedRows, events: updatedEvents } };
      if (prev.timer_state.state === 'ready') {
        const newLevelTime = timeStringToSeconds(updatedRows[0]?.time);
        newState.timer_state = { ...prev.timer_state, remaining: newLevelTime || 0, pausedAt: newLevelTime || 0 };
      }
      return newState;
    });
    touchStructure();
  }, [setTournamentState, touchStructure]);

  const insertColumnAfter = useCallback((colIndex) => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const newColName = window.prompt(t('structure.newColumnPrompt'));
    if (!newColName || newColName.trim() === "") return;

    if (currentState.structure.columns.some(c => c.label === newColName)) {
      showNotification(t('errors.columnNameMustBeUnique'), 'warning');
      return;
    }

    const newId = `col-${newColName.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`;
    setTournamentState(prev => {
      const newColumns = [...prev.structure.columns];
      newColumns.splice(colIndex + 1, 0, { id: newId, label: newColName });
      const newRows = prev.structure.levels.map(row => ({ ...row, [newColName]: "" }));
      return { ...prev, structure: { ...prev.structure, columns: newColumns, levels: newRows } };
    });
    touchStructure();
  }, [setTournamentState, touchStructure, t, showNotification, stateRef]);

  const deleteColumn = useCallback((colIndex) => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const colToDelete = currentState.structure.columns[colIndex];
    if (!colToDelete) return;

    if (colToDelete.label === appConfig.requiredLevelKey || colToDelete.label === appConfig.requiredTimeKey) {
      showNotification(t('errors.cannotDeleteRequiredColumn'), 'error');
      return;
    }

    if (window.confirm(t('structure.confirmDeleteColumn', { column: colToDelete.label }))) {
      const colLabelToDelete = colToDelete.label;
      setTournamentState(prev => {
        const newColumns = prev.structure.columns.filter((_, i) => i !== colIndex);
        const newRows = prev.structure.levels.map(row => {
          const { [colLabelToDelete]: _, ...rest } = row;
          return rest;
        });
        return { ...prev, structure: { ...prev.structure, columns: newColumns, levels: newRows } };
      });
      touchStructure();
    }
  }, [setTournamentState, touchStructure, t, showNotification, appConfig, stateRef]);

  const renameColumn = useCallback((colId, newLabel) => {
    if (!newLabel) return;
    const currentState = stateRef.current;
    if (!currentState) return;

    const colToRename = currentState.structure.columns.find(c => c.id === colId);
    if (!colToRename) return;
    if (colToRename.label === newLabel) return;

    if (currentState.structure.columns.some(c => c.label === newLabel && c.id !== colId)) {
      showNotification(t('errors.columnNameMustBeUnique'), 'warning');
      return;
    }

    const oldLabel = colToRename.label;
    setTournamentState(prev => {
      const newColumns = prev.structure.columns.map(c => c.id === colId ? { ...c, label: newLabel } : c);
      const newRows = prev.structure.levels.map(row => {
        const { [oldLabel]: value, ...rest } = row;
        return { ...rest, [newLabel]: value };
      });
      return { ...prev, structure: { ...prev.structure, columns: newColumns, levels: newRows } };
    });
    touchStructure();
  }, [setTournamentState, touchStructure, showNotification, t, stateRef]);

  const handleColumnDragEnd = useCallback((active, over) => {
    if (!over || active.id === over.id) return;
    setTournamentState(prev => {
      const newColumns = reorderColumns(prev.structure.columns, active.id, over.id, appConfig);
      return { ...prev, structure: { ...prev.structure, columns: newColumns } };
    });
    touchStructure();
  }, [setTournamentState, touchStructure, appConfig]);

  const initializeStructure = useCallback(() => {
    if (window.confirm(t('structure.confirmInitializeStructure'))) {
      setTournamentState(prev => ({ ...getDefaultTournamentData(), id: prev.id }));
      touchStructure();
    }
  }, [setTournamentState, touchStructure, t]);

  const updateEventsForRow = useCallback((rowIndex, messages) => {
    setTournamentState(prev => {
      const otherEvents = (prev.structure.events || []).filter(e => e.rowIndex !== rowIndex);
      const newEventsForThisRow = (messages || [])
        .filter(msg => (msg.message || msg.text || '').trim() !== "")
        .map(msg => ({ rowIndex, message: msg.message || msg.text }));
      const updatedEvents = [...otherEvents, ...newEventsForThisRow].sort((a, b) => a.rowIndex - b.rowIndex);
      return { ...prev, structure: { ...prev.structure, events: updatedEvents } };
    });
    touchStructure();
  }, [setTournamentState, touchStructure]);

  const saveStructureToDb = useCallback(async () => {
    const currentState = stateRef.current;
    if (!currentState || !currentState.id) return false;

    const result = await syncStructureToDB(
      currentState.name,
      currentState.details,
      currentState.structure,
      currentState.timer_state
    );

    if (!result || !result.success) {
      const errorMsg = result?.error?.message || result?.error || 'Unknown error';
      showNotification(`${t('errors.updateFailed')}: ${errorMsg}`, 'error');
      return false;
    }
    return true;
  }, [stateRef, syncStructureToDB, showNotification, t]);

  const setTournamentName = useCallback(async (newName) => {
    setTournamentState(prev => ({ ...prev, name: newName }));
    if (tournamentId && updateName) {
      await updateName(newName);
    }
  }, [setTournamentState, tournamentId, updateName]);

  const setTournamentDetails = useCallback(async (newDetails) => {
    setTournamentState(prev => {
      const updatedDetails = { ...prev.details, ...newDetails };

      // Sync to DB
      if (tournamentId && updateDetails) {
        updateDetails(updatedDetails);
      }

      return {
        ...prev,
        details: updatedDetails
      };
    });
  }, [setTournamentState, tournamentId, updateDetails]);

  const updateInfoText = useCallback(async (text) => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const newDetails = { ...currentState.details, customInfoText: text };

    setTournamentState(prev => ({
      ...prev,
      details: newDetails
    }));

    if (currentState.id) {
      const result = await syncStructureToDB(
        currentState.name,
        newDetails,
        currentState.structure,
        currentState.timer_state
      );
      if (!result || !result.success) {
        const errorMsg = result?.error?.message || result?.error || 'Unknown error';
        showNotification(`${t('errors.updateFailed')}: ${errorMsg}`, 'error');
      }
    }
  }, [stateRef, setTournamentState, syncStructureToDB, showNotification, t]);

  return {
    setTournamentName,
    setTournamentDetails,
    updateTournamentDetails: setTournamentDetails,
    updateRowValue,
    insertRowAfter,
    deleteRow,
    insertBreakRow,
    handleRowDragEnd,
    insertColumnAfter,
    deleteColumn,
    renameColumn,
    handleColumnDragEnd,
    initializeStructure,
    updateEventsForRow,
    touchStructure,
    saveStructureToDb,
    updateInfoText,
  };
};